import { ChevronDown, ChevronUp } from 'lucide-react'
import { useEffect, useState } from 'react'
import { Button } from '@/components/ui/button'
import { MailDetailDialog } from '../MailDetailDialog'
import { amsterdamDay, shortDay, timeOfDay } from '../record/format'
import { getThread } from './api'
import type { RelationRef, ThreadMessage, TimelineMailThread } from './types'

/** "3 berichten · 2 ontvangen · 1 verstuurd" (+ " · met Jan, Piet" on an organisation page). */
function summaryLine(thread: TimelineMailThread, showPersons: boolean): string {
  const parts = [`${thread.count} ${thread.count === 1 ? 'bericht' : 'berichten'}`]
  if (thread.inbound > 0) parts.push(`${thread.inbound} ontvangen`)
  if (thread.outbound > 0) parts.push(`${thread.outbound} verstuurd`)
  if (showPersons && thread.persons.length > 0) {
    parts.push(`met ${thread.persons.map((person) => person.name).join(', ')}`)
  }
  return parts.join(' · ')
}

/**
 * One mail conversation in the timeline (ED-22): subject, how many messages and in which
 * direction; opened it lists the messages (loaded once), and a message opens in the mail
 * dialog. "Alles uitklappen" on the timeline opens every conversation.
 */
export function MailThreadItem({
  thread,
  relation,
  includeMembers,
  expandAll,
  showPersons,
}: {
  thread: TimelineMailThread
  relation: RelationRef
  includeMembers: boolean
  expandAll: boolean
  showPersons: boolean
}) {
  const { kind, id } = relation
  const [open, setOpen] = useState(expandAll)
  const [lastExpandAll, setLastExpandAll] = useState(expandAll)
  const [messages, setMessages] = useState<ThreadMessage[] | null>(null)
  const [failed, setFailed] = useState(false)
  const [attempt, setAttempt] = useState(0)
  const [reading, setReading] = useState<ThreadMessage | null>(null)

  // "Alles uitklappen" follows through ("adjusting state when a prop changes").
  if (lastExpandAll !== expandAll) {
    setLastExpandAll(expandAll)
    setOpen(expandAll)
  }

  useEffect(() => {
    if (!open || messages !== null) return
    let cancelled = false
    getThread({ kind, id }, thread.key, includeMembers)
      .then((found) => {
        if (cancelled) return
        setMessages(found)
        setFailed(false)
      })
      .catch(() => {
        if (!cancelled) setFailed(true)
      })
    return () => {
      cancelled = true
    }
  }, [open, messages, kind, id, thread.key, includeMembers, attempt])

  return (
    <div className="flex min-w-0 flex-1 flex-col gap-0.5">
      <div className="flex items-start justify-between gap-2">
        <div className="flex min-w-0 flex-col gap-0.5">
          <p className="text-sm font-medium break-words">
            <time dateTime={thread.last_at} className="font-normal text-muted-foreground">
              {`${shortDay(thread.day)} ${timeOfDay(thread.last_at)}`}
            </time>
            {` · E-mailgesprek · ${thread.subject ? `'${thread.subject}'` : '(geen onderwerp)'}`}
          </p>
          <p className="text-xs text-muted-foreground">{summaryLine(thread, showPersons)}</p>
          {!open && thread.snippet ? <p className="truncate text-xs text-muted-foreground">{thread.snippet}</p> : null}
        </div>
        <Button
          type="button"
          variant="ghost"
          size="icon-xs"
          aria-expanded={open}
          aria-label={open ? 'Gesprek inklappen' : 'Gesprek uitklappen'}
          onClick={() => setOpen(!open)}
        >
          {open ? <ChevronUp aria-hidden /> : <ChevronDown aria-hidden />}
        </Button>
      </div>

      {open ? (
        messages !== null ? (
          <ul className="mt-1 flex flex-col gap-1 border-l border-border pl-3">
            {messages.map((message) => (
              <li key={message.id}>
                <button
                  type="button"
                  onClick={() => setReading(message)}
                  className="flex w-full flex-col rounded-md px-2 py-1.5 text-left hover:bg-muted max-md:min-h-11"
                >
                  <span className="text-sm font-medium break-words">
                    {`${message.direction === 'inbound' ? 'Ontvangen' : 'Verstuurd'} · ${shortDay(amsterdamDay(message.at))} ${timeOfDay(message.at)} · ${message.counterparty ?? 'onbekend'}`}
                  </span>
                  {message.snippet ? (
                    <span className="line-clamp-2 text-xs text-muted-foreground">{message.snippet}</span>
                  ) : null}
                </button>
              </li>
            ))}
          </ul>
        ) : failed ? (
          <div className="mt-1 flex flex-wrap items-center gap-2">
            <p className="text-sm text-muted-foreground">Gesprek laden mislukt.</p>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => {
                setFailed(false)
                setAttempt((count) => count + 1)
              }}
            >
              Opnieuw proberen
            </Button>
          </div>
        ) : (
          <p className="mt-1 text-sm text-muted-foreground">Laden…</p>
        )
      ) : null}

      <MailDetailDialog
        personId={reading?.person_id ?? ''}
        messageId={reading?.id ?? null}
        open={reading !== null}
        onOpenChange={(next) => {
          if (!next) setReading(null)
        }}
      />
    </div>
  )
}
